import React from 'react';
import Dialog from '@react/react-spectrum/Dialog';
import Select from '@react/react-spectrum/Select';
import TextField from '@react/react-spectrum/Textfield';

import { IMSAccountService } from '../../../service/ims/IMSAccountService';
import { getSelectedDistributor } from '../../../service/ApiService';
import { getCustomerById } from '../../../providers/ResourcesAPI';

export interface CreateLeadFormData {
  leadSource: string;
  intent: string;
  createdAt: string;
  expiryDate: string;
  customerAccountId: string;
  partnerId: string;
  partnerName: string;
  sourceRequestedBy: string;
  resellerQuoteNeeded: boolean;
}

interface CreateLeadDialogProps {
  isOpen: boolean;
  isSubmitting?: boolean;
  customerAccountId?: string;
  handleConfirm: (formData: CreateLeadFormData) => void;
  handleDismiss: () => void;
}

const ALL_INTENT_OPTIONS = [
  { label: 'New', value: 'NEW' },
  { label: 'Renew', value: 'RENEW' }
];

const toISO = (d: Date): string =>
  d.toISOString().replace(/\.\d{3}Z$/, 'Z');

function isInRenewalWindow(cotermDate: string): boolean {
  if (!cotermDate) return false;
  const coterm = new Date(cotermDate);
  coterm.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const windowStart = new Date(coterm);
  windowStart.setDate(windowStart.getDate() - 30);
  return today >= windowStart && today <= coterm;
}

function CreateLeadDialog({
  isOpen,
  isSubmitting = false,
  customerAccountId: prefilledAccountId = '',
  handleConfirm,
  handleDismiss,
}: Readonly<CreateLeadDialogProps>) {

  const [intent, setIntent] = React.useState('NEW');
  const [customerAccountId, setCustomerAccountId] = React.useState(prefilledAccountId);
  const [isRenewEligible, setIsRenewEligible] = React.useState(false);
  const [renewCheckNote, setRenewCheckNote] = React.useState('');

  React.useEffect(() => {
    if (!customerAccountId.trim()) {
      setIsRenewEligible(false);
      setRenewCheckNote('');
      setIntent('NEW');
      return;
    }
    const timer = setTimeout(() => {
      getCustomerById(customerAccountId).then((customer: any) => {
        if (customer.httpstatus === 200 && customer.cotermDate) {
          const eligible = isInRenewalWindow(customer.cotermDate);
          setIsRenewEligible(eligible);
          setRenewCheckNote(eligible
            ? `Renewal window open (coterm: ${customer.cotermDate})`
            : `Not in AD-30 window (coterm: ${customer.cotermDate})`);
          if (!eligible && intent === 'RENEW') setIntent('NEW');
        } else {
          setIsRenewEligible(false);
          setRenewCheckNote('');
        }
      }).catch(() => {
        setIsRenewEligible(false);
        setRenewCheckNote('');
      });
    }, 600);
    return () => clearTimeout(timer);
  }, [customerAccountId]);

  const intentOptions = isRenewEligible
    ? ALL_INTENT_OPTIONS
    : ALL_INTENT_OPTIONS.filter(o => o.value !== 'RENEW');

  const loggedInEmail = IMSAccountService.getUserProfile()?.email || '';
  const selectedDistributor = getSelectedDistributor();

  const handleSubmit = () => {
    const now = new Date();
    handleConfirm({
      leadSource: 'SANDBOX',
      intent,
      createdAt: toISO(now),
      expiryDate: toISO(new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)),
      customerAccountId,
      partnerId: selectedDistributor?.distributorId || '',
      partnerName: selectedDistributor?.distributorName || '',
      sourceRequestedBy: loggedInEmail,
      resellerQuoteNeeded: false
    });
  };

  return (
    <Dialog
      title='Create Lead'
      open={isOpen}
      confirmLabel={isSubmitting ? 'Submitting...' : 'Submit'}
      confirmDisabled={isSubmitting}
      cancelLabel='Cancel'
      onConfirm={handleSubmit}
      onCancel={handleDismiss}
    >
      <div>
        <br />
        Intent :&nbsp;
        <Select
          aria-label='Intent'
          value={intent}
          options={intentOptions}
          onSelect={(value: string) => setIntent(value)}
        />

        <br /><br />
        <b>Customer</b>
        <br /><br />
        {prefilledAccountId ? (
          <div style={{ marginTop: '4px', padding: '10px', background: '#f5f5f5', borderRadius: '4px', fontSize: '13px' }}>
            <b>Account ID:</b> {prefilledAccountId}
          </div>
        ) : (
          <>
            Account ID :&nbsp;
            <TextField
              placeholder='Customer Account ID'
              value={customerAccountId}
              onChange={(value: string) => setCustomerAccountId(value)}
            />
          </>
        )}
        {renewCheckNote && (
          <div style={{ fontSize: '12px', marginTop: '4px',
            color: isRenewEligible ? '#2d9e2d' : '#888' }}>
            {renewCheckNote}
          </div>
        )}

        <br /><br />
        <b>Partner</b>
        <br />
        <div style={{ marginTop: '8px', padding: '10px', background: '#f5f5f5', borderRadius: '4px', fontSize: '13px' }}>
          <b>Name:</b> {selectedDistributor?.distributorName || '—'}<br />
          <b>ID:</b> {selectedDistributor?.distributorId || 'not selected'}
        </div>

      </div>
    </Dialog>
  );
}

export default CreateLeadDialog;
